import { useEffect, useState } from 'react';
import { Plug, CheckCircle2, AlertCircle, RefreshCw, Send, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const IntegrationsPanel = () => {
  const [st, setSt] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/integrations/zoho/status').then(r => setSt(r.data)).catch(e => toast.error(e?.response?.data?.detail || 'Could not load integration status'));
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get('zoho') === 'connected') toast.success('Zoho CRM connected');
    if (p.get('zoho') === 'error') toast.error(`Zoho authorisation failed: ${p.get('reason')}`);
  }, []);

  const connect = async () => {
    try { const { data } = await api.get('/integrations/zoho/connect-url'); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start Zoho authorisation'); }
  };
  const test = async (sendEmail) => {
    if (!testEmail) return toast.error('Enter a test email');
    setBusy(true);
    try {
      const { data } = await api.post('/integrations/zoho/test', { email: testEmail, first_name: 'Adoptic', last_name: 'Test', send_email: sendEmail });
      toast.success(sendEmail ? `Pipeline run: email ${data.welcome_email.ok ? 'sent' : 'FAILED'}, lead ${data.zoho_lead.ok ? 'created' : 'FAILED'}` : `Zoho lead created: ${data.lead_id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); }
    finally { setBusy(false); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };

  if (!st) return <div className="font-body text-sm text-ink/50 p-4">Loading…</div>;
  const webhookUrl = `${API_BASE}/api/integrations/signup-webhook`;
  const snippet = `curl -X POST ${webhookUrl} \\\n  -H "Content-Type: application/json" \\\n  -H "X-Signup-Secret: $SIGNUP_WEBHOOK_SECRET" \\\n  -d '{"user_email":"jane@example.com","first_name":"Jane","last_name":"Doe"}'`;

  return (
    <div className="space-y-4" data-testid="integrations-panel">
      <div className="solid-card p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Plug className="w-4 h-4 text-brand-orange" />
            <h3 className="font-display text-base font-extrabold text-ink">Zoho CRM — Leads</h3>
            {st.connected
              ? <span className="inline-flex items-center gap-1 font-body text-[11px] text-forest" data-testid="zoho-status-connected"><CheckCircle2 className="w-3.5 h-3.5" /> Connected · {st.api_domain}</span>
              : <span className="inline-flex items-center gap-1 font-body text-[11px] text-amber" data-testid="zoho-status-disconnected"><AlertCircle className="w-3.5 h-3.5" /> {st.status === 'reauth_required' ? 'Re-authorisation required' : 'Not connected'}</span>}
          </div>
          <p className="font-body text-xs text-ink/60 mt-1">New signups create a Lead: name, email, Lead Source "{st.lead_source}", Lead Status "{st.lead_status}". Scope: {st.scopes}. {st.last_error && <span className="text-danger">Last error: {st.last_error}</span>}</p>
        </div>
        <Button onClick={connect} data-testid="zoho-connect-btn" className="bg-ink hover:bg-ink/90 text-white font-body text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{st.connected ? 'Re-authorise Zoho' : 'Connect Zoho CRM'}</Button>
      </div>

      <div className="solid-card p-5">
        <h3 className="font-display text-sm font-extrabold text-ink mb-1">Signup webhook (for external backends)</h3>
        <p className="font-body text-xs text-ink/60 mb-3">POST JSON with <code>user_email</code>, <code>first_name</code>, <code>last_name</code> and header <code>X-Signup-Secret</code> (value = SIGNUP_WEBHOOK_SECRET in backend .env). Returns 202; the welcome email (Resend, exponential backoff) and Zoho lead run in parallel — a Zoho failure never blocks the email and triggers an alert to {'ALERT_EMAIL'}.</p>
        <div className="flex items-center gap-2 mb-2">
          <code className="font-body text-xs bg-ink/[0.04] px-2 py-1 rounded flex-1 truncate" data-testid="webhook-url">{webhookUrl}</code>
          <Button size="sm" variant="outline" onClick={() => copy(webhookUrl)} className="h-7 font-body text-xs"><Copy className="w-3 h-3 mr-1" />Copy</Button>
        </div>
        <pre className="font-mono text-[11px] bg-ink text-white/90 rounded-[6px] p-3 overflow-x-auto">{snippet}</pre>
      </div>

      <div className="solid-card p-5">
        <h3 className="font-display text-sm font-extrabold text-ink mb-2">Test the pipeline</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@yourdomain.com" className="font-body text-sm" data-testid="integration-test-email" />
          <Button disabled={busy} onClick={() => test(false)} variant="outline" className="font-body text-xs" data-testid="zoho-test-lead-btn">Create test lead only</Button>
          <Button disabled={busy} onClick={() => test(true)} className="bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs" data-testid="pipeline-test-btn"><Send className="w-3.5 h-3.5 mr-1.5" />Run full pipeline</Button>
        </div>
        {st.recent_runs?.length > 0 && (
          <table className="w-full mt-4 font-body text-xs" data-testid="onboarding-runs">
            <thead><tr className="text-left text-ink/40 uppercase tracking-wider text-[10px]"><th className="py-1">When</th><th>Email</th><th>Source</th><th>Welcome email</th><th>Zoho lead</th></tr></thead>
            <tbody>{st.recent_runs.map(r => (
              <tr key={r.run_id} className="border-t border-ink/5">
                <td className="py-1.5 text-ink/60">{new Date(r.started_at).toLocaleString()}</td><td>{r.email}</td><td className="text-ink/60">{r.source}</td>
                <td className={r.welcome_email.ok ? 'text-forest' : 'text-danger'} title={typeof r.welcome_email.detail === 'string' ? r.welcome_email.detail : ''}>{r.welcome_email.ok ? 'sent' : 'failed'}</td>
                <td className={r.zoho_lead.ok ? 'text-forest' : 'text-danger'} title={typeof r.zoho_lead.detail === 'string' ? r.zoho_lead.detail : ''}>{r.zoho_lead.ok ? `created${r.zoho_lead.detail?.lead_id ? ` · ${r.zoho_lead.detail.lead_id}` : ''}` : 'failed'}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};
